import { useApi } from 'soykajs/composables/api'

/** secure_filename документов, ожидающих soft-delete при Save карточки. */
const pendingSecureFilenames = new Set()

/**
 * Удаления до soft-delete файлов. СЗ должна уйти раньше ПП:
 * пока записка есть, платёжное поручение снять нельзя.
 * @type {Array<() => Promise<void>>}
 */
const pendingBeforeUploadActions = []

/**
 * Кастомные удаления после файлов. EPR снимается после ПП:
 * пока поручение есть, EPR архивировать нельзя.
 * @type {Array<() => Promise<void>>}
 */
const pendingRemoveActions = []

/**
 * Ставит upload в очередь на удаление при следующем сохранении карточки.
 *
 * @param {string|null|undefined} secureFilename
 */
export function queueUploadRemove(secureFilename) {
  if (!secureFilename) {
    return
  }
  pendingSecureFilenames.add(String(secureFilename))
}

/**
 * Ставит произвольное удаление (entity DELETE) в очередь на Save.
 *
 * @param {() => Promise<void>} action
 * @param {{ beforeUploads?: boolean }} [options] beforeUploads — до удаления файлов.
 */
export function queueDeferredRemoveAction(action, options = {}) {
  if (typeof action !== 'function') {
    return
  }
  const list = options.beforeUploads ? pendingBeforeUploadActions : pendingRemoveActions
  list.push(action)
}

/**
 * Снимает upload с очереди (отмена редактирования / возврат документа с сервера).
 *
 * @param {string|null|undefined} secureFilename
 */
export function cancelUploadRemove(secureFilename) {
  if (!secureFilename) {
    return
  }
  pendingSecureFilenames.delete(String(secureFilename))
}

/**
 * Есть ли отложенные удаления документов.
 *
 * @returns {boolean}
 */
export function hasPendingUploadRemoves() {
  return (
    pendingSecureFilenames.size > 0
    || pendingBeforeUploadActions.length > 0
    || pendingRemoveActions.length > 0
  )
}

/**
 * Выполняет действия очереди. Неудачные возвращает обратно, чтобы следующий Save повторил их.
 *
 * @param {Array<() => Promise<void>>} actions
 * @param {Array<() => Promise<void>>} queue
 * @param {unknown[]} errors
 */
async function runRemoveActions(actions, queue, errors) {
  const failed = []
  for (const action of actions) {
    try {
      await action()
    } catch (err) {
      errors.push(err)
      failed.push(action)
    }
  }
  if (failed.length) {
    queue.unshift(...failed)
  }
}

/**
 * Soft-delete файлов и кастомные удаления перед Save.
 * Сначала СЗ, затем файлы (ПП), затем EPR.
 *
 * @returns {Promise<void>}
 */
export async function flushPendingUploadRemoves() {
  const before = pendingBeforeUploadActions.splice(0, pendingBeforeUploadActions.length)
  const names = [...pendingSecureFilenames]
  const actions = pendingRemoveActions.splice(0, pendingRemoveActions.length)
  if (!before.length && !names.length && !actions.length) {
    return
  }

  const errors = []

  await runRemoveActions(before, pendingBeforeUploadActions, errors)

  if (names.length) {
    const { refresh: doRemove } = useApi({ method: 'delete' })
    for (const secureFilename of names) {
      try {
        await doRemove({ url: `upload/${secureFilename}` })
        pendingSecureFilenames.delete(secureFilename)
      } catch (err) {
        errors.push(err)
      }
    }
  }

  await runRemoveActions(actions, pendingRemoveActions, errors)

  if (errors.length) {
    throw errors[0]
  }
}

/**
 * Очищает очередь без запросов к API (сброс формы / уход со страницы).
 */
export function clearPendingUploadRemoves() {
  pendingSecureFilenames.clear()
  pendingBeforeUploadActions.length = 0
  pendingRemoveActions.length = 0
}
